import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
  text-align: center;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const SongTitle = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AmountControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const AmountButton = styled.button`
  background: ${props => props.primary ? '#cc0000' : '#444'};
  color: white;
  border: none;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  font-size: 2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? '#aa0000' : '#555'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AmountDisplay = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #ffffff;
  min-width: 120px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #cc0000;
`;

function SongPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSong();
  }, [id]);

  const loadSong = async () => {
    try {
      setLoading(true);
      const songData = await songAPI.getSong(id);
      setSong(songData);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustAmount = async (adjustment) => {
    try {
      const updatedSong = await songAPI.adjustAmount(id, adjustment);
      setSong(updatedSong);
    } catch (error) {
      console.error('Error adjusting amount:', error);
      toast.error('Failed to adjust amount');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!song) {
    return (
      <PageContainer>
        <ErrorMessage>
          <h2>Song not found</h2>
          <p>The song you're looking for doesn't exist.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/')}>
        ← All Songs
      </BackButton>

      <SongTitle>{song.name}</SongTitle>

      <AmountControls>
        <AmountButton onClick={() => handleAdjustAmount(-1)}>
          −
        </AmountButton>
        <AmountDisplay>{song.current_amount}</AmountDisplay>
        <AmountButton primary onClick={() => handleAdjustAmount(1)}>
          +
        </AmountButton>
      </AmountControls>
    </PageContainer>
  );
}

export default SongPage;